import ContextMenu from 'components/ContextMenu/ContextMenu';
import ProductSidebar from 'components/ProductSidebar/ProductSidebar';
import Scene from 'components/Scene/Scene';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import SceneConflicts from 'components/SceneConflicts';
import { rem, stripUnit } from 'polished';
import SceneLoadingScreen from 'components/SceneLoadingScreen/SceneLoadingScreen';
import { isMobile } from 'utils/platform';
import BannerMessage from 'components/BannerMessage/BannerMessage';
import media from 'ui/media';
import MobileMenu from 'components/ProductSidebar/MobileMenu/MobileMenu';
import SelectionOverrideIndicators from 'components/SelectionOverrideIndicators';
import SceneMenu from 'components/SceneMenu/SceneMenu';
import { SelectedProvider } from 'contexts/selected';
import ConfigurationHeader from 'components/Header/ConfigurationHeader';
import useChatClient from 'hooks/useChatClient';
import { useLocation } from 'react-router-dom';
import { AppRoutes } from 'routes';
import { isBigScreenPlanningEnabled, isRoomConfigurationActive } from 'ducks/layout';
import { useSelector } from 'react-redux';
import SceneEntityTabButtons from 'components/SceneEntityTabButtons';
import MoveButtonsMenu from 'components/MoveButtonsMenu';
import SkipButton from 'ui/SkipButton/SkipButton';
import { useIntl } from 'react-intl';
import useMemoryMonitoring from 'hooks/useMemoryMonitoring';
import { useServiceSettings } from 'contexts/serviceSettings';

const Root = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1;

  @media print {
    display: none;
  }
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;
  ${media.mobile`
    flex-direction: column;
  `};
`;

const MessageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  margin-right: ${({ theme }) => theme.layout.sidebarWidth};
  z-index: 100;
  ${media.mobile`
    margin-right: 0;
  `};
`;

const SceneSpace = styled.section`
  position: relative;
  flex-grow: 1;
  height: 100%;
`;

const StyledSkipButton = styled(SkipButton)`
  position: absolute;
  top: 0.8rem;
  left: 6rem;
`;

const Planner = () => {
  const sidebarWidth = isMobile() ? 0 : 480;
  const sidebarWidhtInRem = stripUnit(rem(sidebarWidth));
  const roomConfigurationActive = useSelector(isRoomConfigurationActive);
  const [currentlyOnPlannerPage, setCurrentlyOnPlannerPage] = useState<boolean>(false);
  const location = useLocation();
  const { formatMessage } = useIntl();
  const { enableKeyboardNavigation } = useServiceSettings().dexfSettings.general;

  useEffect(() => {
    if (location.pathname === AppRoutes.PLANNER) {
      setCurrentlyOnPlannerPage(true);
    } else {
      setCurrentlyOnPlannerPage(false);
    }
  }, [currentlyOnPlannerPage, location.pathname]);

  useChatClient();
  useMemoryMonitoring();

  const isBigPlanner = useSelector(isBigScreenPlanningEnabled);

  return (
    <Root
      style={{
        visibility: currentlyOnPlannerPage ? 'visible' : 'hidden',
        pointerEvents: currentlyOnPlannerPage ? 'auto' : 'none'
      }}
    >
      <Main>
        <SelectedProvider>
          {enableKeyboardNavigation && (
            <>
              <StyledSkipButton
                mb="1rem"
                dataTestId="skip-button-3D-scene"
                skipToId="product-sidebar"
                text={formatMessage({ id: 'storageone.skipButton3dScene' })}
              />
              <StyledSkipButton
                mb="1rem"
                dataTestId="skip-button-3D-scene"
                skipToId="scene-section"
                text={formatMessage({ id: 'storageone.skipButtonProductListTop' })}
              />
            </>
          )}
          {!isBigPlanner && <ConfigurationHeader hideMenu={!currentlyOnPlannerPage} />}

          <SceneSpace id="scene-section">
            <SceneLoadingScreen sidebarWidthRems={sidebarWidhtInRem} />
            <Scene />
            {currentlyOnPlannerPage && (
              <>
                <MessageWrapper>
                  <BannerMessage />
                </MessageWrapper>
                <SceneConflicts />
                <SelectionOverrideIndicators />
                {enableKeyboardNavigation && !roomConfigurationActive && <SceneEntityTabButtons />}
                <MoveButtonsMenu />
                {!isMobile() && !isBigPlanner && <ContextMenu />}
              </>
            )}
          </SceneSpace>
          {isMobile() ? <MobileMenu /> : !isBigPlanner && <ProductSidebar />}
          {currentlyOnPlannerPage && !isBigPlanner && <SceneMenu />}
        </SelectedProvider>
      </Main>
    </Root>
  );
};

export default Planner;
